// Edit day (owner, 2026-11-03): one whole-day change in place of the six
// correction dialogs. The server holds every rule —
// 20261103120000_edit_day.sql (day_edit_state, edit_day, request_day_edit,
// decide_day_edit). Nothing here grants anything; it shapes the calls and
// says the server's answers in words.
import { supabase } from './supabase';

/** policy.editDay.rollout: which dialog shows. 'hr' = HR and managers first. */
export type EditDayRollout = 'off' | 'hr' | 'everyone';

/** A session as the server shows it: org-local 'HH:MM', out null while open. */
export type DaySession = {
  id: string;
  in: string;
  out: string | null;
  seconds: number;
  status: 'running' | 'paused' | 'logged';
  system_closed: boolean;
};
export type DayProject = {
  project_id: string;
  task_id: string | null;
  project: string;
  task: string | null;
  seconds: number;
};
export type DayCredit = {
  id: string;
  hours: number;
  category: string;
  note: string | null;
  status: 'approved' | 'pending';
};
/** A day's sessions, project split and credits (day_edit_snapshot). */
export type DaySnapshot = { sessions: DaySession[]; projects: DayProject[]; credits: DayCredit[] };

export type DayEdit = {
  id: string;
  profile_id: string;
  work_date: string;
  kind: 'direct' | 'request';
  status: 'pending' | 'approved' | 'rejected';
  before: DaySnapshot;
  proposed: DayChange;
  applied: DaySnapshot | null;
  reason: string;
  decline_reason: string | null;
  created_at: string;
};
/** A waiting request, with the requester's name for a queue. */
export type PendingDayEdit = DayEdit & { full_name: string | null; manager_id: string | null };

export type DayState = DaySnapshot & {
  locked: boolean;
  amend_open: boolean;
  /** direct: you change it now; request: you ask; read: neither. */
  mode: 'direct' | 'request' | 'read';
  pending: DayEdit | null;
};

/** The whole-day change. A key left out leaves that part of the day alone. */
export type DayChange = {
  sessions?: { id?: string; in: string; out: string }[];
  credits?: { id?: string; hours: number; category: string; note?: string | null }[];
  projects?: { project_id: string; task_id: string | null; seconds: number }[];
};

type Result<T = null> = { data: T | null; error: { message: string } | null };

const call = async <T>(fn: string, args: Record<string, unknown>): Promise<Result<T>> => {
  if (!supabase) return { data: null, error: { message: 'Not connected.' } };
  try {
    const { data, error } = await supabase.rpc(fn, args);
    return { data: (data as T) ?? null, error };
  } catch (e) {
    // A dropped connection rejects instead of returning an error: same answer.
    return { data: null, error: { message: (e as Error).message || 'network error' } };
  }
};

export const getDayState = (profileId: string, date: string) =>
  call<DayState>('day_edit_state', { p_profile: profileId, p_date: date });

export const editDay = (profileId: string, date: string, change: DayChange, reason: string) =>
  call<string>('edit_day', {
    p_profile: profileId,
    p_date: date,
    p_change: change,
    p_reason: reason,
  });

export const requestDayEdit = (date: string, change: DayChange, reason: string) =>
  call<string>('request_day_edit', { p_date: date, p_change: change, p_reason: reason });

/** change null approves what was asked; otherwise the whole adjusted day. */
export const decideDayEdit = (
  id: string,
  approve: boolean,
  change: DayChange | null,
  note: string | null,
) => call('decide_day_edit', { p_id: id, p_approve: approve, p_change: change, p_note: note });

export async function cancelDayEdit(id: string): Promise<{ error: { message: string } | null }> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.from('day_edits').delete().eq('id', id);
  return { error };
}

/** Waiting requests the caller may read (RLS: HR sees all, a manager their reports'). */
export async function listPendingDayEdits(): Promise<PendingDayEdit[] | null> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('day_edits')
    .select('*, profiles!day_edits_profile_id_fkey(full_name, manager_id)')
    .eq('status', 'pending')
    .order('created_at', { ascending: true });
  if (error) return null;
  return (
    (data as (DayEdit & { profiles: { full_name: string | null; manager_id: string | null } })[]) ??
    []
  ).map(({ profiles, ...r }) => ({
    ...r,
    full_name: profiles?.full_name ?? null,
    manager_id: profiles?.manager_id ?? null,
  }));
}

// ── Pure helpers, shared by the dialog and its tests ─────────────────────────

const mins = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));

/** "09:00–13:00, 14:00–18:00", or "no time". */
export const sessionsText = (s: readonly { in: string; out: string | null }[]): string =>
  s.length ? s.map((x) => `${x.in}–${x.out ?? 'now'}`).join(', ') : 'no time';

/**
 * What is wrong with a day's sessions, in the dialog's words, or null. The
 * server checks the same (and breaks, which only it knows); this catches it
 * before the round trip. Sorted by check-in, each must end before the next.
 */
export function sessionProblem(s: readonly { in: string; out: string }[]): string | null {
  if (s.some((x) => !x.in || !x.out)) return 'Every session needs a check-in and a check-out.';
  if (s.some((x) => mins(x.out) <= mins(x.in))) return 'Check-out has to be after check-in.';
  const sorted = [...s].sort((a, b) => mins(a.in) - mins(b.in));
  for (let i = 1; i < sorted.length; i++)
    if (mins(sorted[i].in) < mins(sorted[i - 1].out))
      return 'Two sessions overlap: each check-in has to come after the last check-out.';
  return null;
}

/** Worked seconds of 'HH:MM' sessions (before breaks the server subtracts). */
export const spanSeconds = (s: readonly { in: string; out: string }[]): number =>
  s.reduce((sum, x) => sum + Math.max(0, mins(x.out) - mins(x.in)) * 60, 0);
